"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Cog, Cpu, Code as Code2, Radio, Gamepad2, Bot, Trophy, ArrowRight, ChevronRight, Zap, CircleCheck as CheckCircle2, Layers, ArrowDown } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface Stage {
  number: string;
  enTitle: string;
  viTitle: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const STAGES: Stage[] = [{
  number: "01",
  enTitle: "Mechanical Thinking",
  viTitle: "Tư duy Cơ khí",
  icon: Cog
}, {
  number: "02",
  enTitle: "Computational Thinking",
  viTitle: "Tư duy Tính toán",
  icon: Cpu
}, {
  number: "03",
  enTitle: "Programming",
  viTitle: "Lập trình",
  icon: Code2
}, {
  number: "04",
  enTitle: "Sensors",
  viTitle: "Cảm biến",
  icon: Radio
}, {
  number: "05",
  enTitle: "Robot Control",
  viTitle: "Điều khiển Robot",
  icon: Gamepad2
}, {
  number: "06",
  enTitle: "Autonomous Robotics",
  viTitle: "Robotics Tự hành",
  icon: Bot
}, {
  number: "07",
  enTitle: "Mission Strategy",
  viTitle: "Chiến thuật Nhiệm vụ",
  icon: Trophy
}];
interface CourseNode {
  code: string;
  enTitle: string;
  viTitle: string;
  enSub: string;
  viSub: string;
  url: string;
}
const D_COURSES: CourseNode[] = [{
  code: "D1",
  enTitle: "Discovery 1",
  viTitle: "Khám Phá 1",
  enSub: "Mechanical Logic & Levers",
  viSub: "Tư duy Cơ khí & Đòn bẩy",
  url: "/RoboticsCourseD1Page"
}, {
  code: "D2",
  enTitle: "Discovery 2",
  viTitle: "Khám Phá 2",
  enSub: "Gears & Transmission",
  viSub: "Bánh răng & Truyền động",
  url: "/RoboticsCourseD2Page"
}, {
  code: "D3",
  enTitle: "Discovery 3",
  viTitle: "Khám Phá 3",
  enSub: "Visual Algorithmic Coding",
  viSub: "Lập trình Khối Thuật toán",
  url: "/RoboticsCourseD3Page"
}, {
  code: "D4",
  enTitle: "Discovery 4",
  viTitle: "Khám Phá 4",
  enSub: "Autonomous Nav & Sensors",
  viSub: "Robot Tự hành & Cảm biến",
  url: "/RoboticsCourseD4Page"
}];
const HP_COURSES: CourseNode[] = [{
  code: "HP1",
  enTitle: "High Perf 1",
  viTitle: "Chuyên Sâu 1",
  enSub: "Core Systems & Python",
  viSub: "Hệ thống Cốt lõi & Python",
  url: "/RoboticsCourseHP1Page"
}, {
  code: "HP2",
  enTitle: "High Perf 2",
  viTitle: "Chuyên Sâu 2",
  enSub: "Micro-controllers & Circuits",
  viSub: "Vi điều khiển & Mạch điện",
  url: "/RoboticsCourseHP2Page"
}, {
  code: "HP3",
  enTitle: "High Perf 3",
  viTitle: "Chuyên Sâu 3",
  enSub: "Gyro & Line Tracing",
  viSub: "Cảm biến Con quay & Dò đường",
  url: "/RoboticsCourseHP3Page"
}, {
  code: "HP4",
  enTitle: "High Perf 4",
  viTitle: "Chuyên Sâu 4",
  enSub: "Arena Missions & Speed",
  viSub: "Thi đấu Sa bàn & Tốc độ",
  url: "/RoboticsCourseHP4Page"
}, {
  code: "HP5",
  enTitle: "High Perf 5",
  viTitle: "Chuyên Sâu 5",
  enSub: "3D CAD & Advanced C++",
  viSub: "Thiết kế CAD 3D & C++",
  url: "/RoboticsCourseHp5Page"
}, {
  code: "HP6",
  enTitle: "High Perf 6",
  viTitle: "Chuyên Sâu 6",
  enSub: "World Olympiad Prep",
  viSub: "Thi đấu Olympic Quốc tế",
  url: "/RoboticsCourseHP6Page"
}];
export default function RoboticsPathway() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const getStoredLang = () => {
      const saved = localStorage.getItem("roboto_lang");
      if (saved === "en" || saved === "vi") {
        setLang(saved);
      }
    };
    getStoredLang();
    const handleLangChange = (e: Event) => {
      const saved = localStorage.getItem("roboto_lang");
      if (saved === "en" || saved === "vi") {
        setLang(saved);
      } else if ((e as CustomEvent).detail?.lang) {
        setLang((e as CustomEvent).detail.lang);
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
    };
  }, []);
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="engineeringtoroboticspathway" data-nav="light" className="relative py-16 md:py-24 bg-background text-foreground overflow-hidden isolate">
      {/* Decorative hairline pattern background */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 opacity-30 bg-[radial-gradient(circle_at_top,_var(--border)_1px,_transparent_1px)] bg-[size:24px_24px]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold tracking-tight text-foreground uppercase">
            {lang === "vi" ? "TỪ TƯ DUY KỸ THUẬT ĐẾN ROBOTICS" : "FROM ENGINEERING THINKING TO ROBOTICS"}
          </h2>
          <div className="mt-4 mx-auto w-16 h-1 bg-primary rounded-full" />
        </div>

        {/* 7-Stage Visual Step Progression */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 md:gap-3 lg:gap-2 relative">
          {STAGES.map((stage, i) => {
          const Icon = stage.icon;
          const isLast = i === STAGES.length - 1;
          return <motion.div key={stage.number} data-index={i} variants={itemVariants} className="relative group flex flex-col h-full">
                <div className="flex-1 bg-card border border-border rounded-xl p-5 flex flex-col justify-between transition-all duration-300 hover:shadow-md hover:border-primary/50 relative z-10">
                  {/* Badge & Icon Header */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-md text-xs font-mono font-semibold bg-muted text-foreground border border-border">
                        {stage.number}
                      </span>
                      <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <Icon className="size-5" />
                      </div>
                    </div>

                    {/* Stage Title */}
                    <h3 className="text-base font-semibold text-foreground leading-snug">
                      {lang === "vi" ? stage.viTitle : stage.enTitle}
                    </h3>
                  </div>

                  {/* Step Accent hairline */}
                  <div className="mt-6 pt-3 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground font-mono">
                    <span>Stage 0{i + 1}</span>
                    {!isLast && <ChevronRight className="size-3.5 text-muted-foreground group-hover:text-primary transition-colors hidden lg:block" />}
                  </div>
                </div>

                {/* Mobile / Tablet Connector Arrow */}
                {!isLast && <div className="flex justify-center my-1 lg:hidden text-muted-foreground/50">
                    <ArrowRight className="size-4 rotate-90 sm:rotate-0" />
                  </div>}
              </motion.div>;
        })}
        </motion.div>

        {/* Technical Deep-Blue Blueprint Pathway Diagram (D1-D4 -> HP1-HP6 Progression) */}
        <div className="mt-14 lg:mt-18 rounded-2xl bg-[#0a1224] border border-blue-900/60 p-6 sm:p-8 lg:p-10 text-white shadow-2xl relative overflow-hidden">
          {/* Blueprint background grid */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-25 bg-[radial-gradient(#38bdf8_1px,transparent_1px)] bg-[size:20px_20px]" />

          {/* Technical Diagram Header */}
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 mb-8 border-b border-blue-900/80">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="size-2 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-xs font-mono tracking-widest text-cyan-400 uppercase font-semibold">
                  ROBOTO LAB // ACADEMIC COURSE PROGRESSION
                </span>
              </div>
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-white tracking-tight">
                {lang === "vi" ? "SƠ ĐỒ TIẾN TRÌNH KHÓA HỌC D1-D4 & HP1-HP6" : "D1-D4 & HP1-HP6 COURSE PROGRESSION ROADMAP"}
              </h3>
            </div>
            <div className="flex items-center gap-3 text-xs font-mono bg-blue-950/80 border border-blue-800/60 rounded-lg px-3.5 py-2 text-slate-300 shrink-0">
              <Layers className="size-4 text-cyan-400" />
              <span>
                {lang === "vi" ? "10 CẤP ĐỘ NÂNG CAO" : "10 PROGRESSIVE LEVELS"}
              </span>
            </div>
          </div>

          {/* Phase 1: D1-D4 Discovery Foundation Track */}
          <div className="relative z-10 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-md text-xs font-mono font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 uppercase">
                {lang === "vi" ? "GIAI ĐOẠN 1: TƯ DUY NỀN TẢNG (D1 – D4)" : "PHASE 1: FOUNDATION TRACK (D1 – D4)"}
              </span>
              <div className="h-px flex-1 bg-gradient-to-r from-cyan-500/40 to-transparent" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
              {D_COURSES.map((course, i) => <div key={course.code} data-index={i} className="group relative flex flex-col">
                  <Link to={course.url} className="flex-1 bg-slate-900/90 border border-slate-800 hover:border-cyan-400/70 rounded-xl p-4 transition-all duration-300 hover:bg-slate-800/90 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md text-xs font-mono font-bold bg-cyan-950 text-cyan-300 border border-cyan-800">
                          {course.code}
                        </span>
                        <ChevronRight className="size-4 text-slate-500 group-hover:text-cyan-400 transition-colors" />
                      </div>
                      <h4 className="text-sm font-semibold text-white group-hover:text-cyan-300 transition-colors">
                        {lang === "vi" ? course.viTitle : course.enTitle}
                      </h4>
                      <p className="text-xs text-slate-400 mt-1 leading-snug">
                        {lang === "vi" ? course.viSub : course.enSub}
                      </p>
                    </div>
                    <div className="mt-4 pt-2.5 border-t border-slate-800/80 flex items-center justify-between text-[11px] font-mono text-slate-500 group-hover:text-cyan-400">
                      <span>Level 0{i + 1}</span>
                      <CheckCircle2 className="size-3.5 text-cyan-500/60" />
                    </div>
                  </Link>
                </div>)}
            </div>
          </div>

          {/* Advancement Transition Indicator */}
          <div className="relative z-10 flex flex-col items-center justify-center my-6">
            <div className="flex items-center gap-3 bg-blue-950/90 border border-cyan-500/50 rounded-full px-5 py-2 text-xs font-mono text-cyan-200 shadow-lg">
              <Zap className="size-4 text-amber-400 fill-amber-400/20" />
              <span className="font-semibold uppercase tracking-wider">
                {lang === "vi" ? "CHUYỂN TIẾP SANG LỘ TRÌNH CHUYÊN SÂU & THI ĐẤU (HP)" : "ADVANCEMENT TO HIGH PERFORMANCE TRACK (HP)"}
              </span>
              <ArrowDown className="size-4 text-cyan-400" />
            </div>
          </div>

          {/* Phase 2: HP1-HP6 High Performance Track */}
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-md text-xs font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 uppercase">
                {lang === "vi" ? "GIAI ĐOẠN 2: CHUYÊN SÂU & THI ĐẤU (HP1 – HP6)" : "PHASE 2: HIGH PERFORMANCE TRACK (HP1 – HP6)"}
              </span>
              <div className="h-px flex-1 bg-gradient-to-r from-amber-500/40 to-transparent" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
              {HP_COURSES.map((course, i) => <div key={course.code} data-index={i} className="group relative flex flex-col">
                  <Link to={course.url} className="flex-1 bg-slate-900/90 border border-slate-800 hover:border-amber-400/70 rounded-xl p-4 transition-all duration-300 hover:bg-slate-800/90 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-md text-xs font-mono font-bold bg-amber-950 text-amber-300 border border-amber-800">
                          {course.code}
                        </span>
                        <ChevronRight className="size-4 text-slate-500 group-hover:text-amber-400 transition-colors" />
                      </div>
                      <h4 className="text-sm font-semibold text-white group-hover:text-amber-300 transition-colors">
                        {lang === "vi" ? course.viTitle : course.enTitle}
                      </h4>
                      <p className="text-xs text-slate-400 mt-1 leading-snug">
                        {lang === "vi" ? course.viSub : course.enSub}
                      </p>
                    </div>
                    <div className="mt-4 pt-2.5 border-t border-slate-800/80 flex items-center justify-between text-[11px] font-mono text-slate-500 group-hover:text-amber-400">
                      <span>Level 0{i + 5}</span>
                      <Trophy className="size-3.5 text-amber-500/70" />
                    </div>
                  </Link>
                </div>)}
            </div>
          </div>
        </div>

        {/* Explanatory Text & CTA */}
        <div className="mt-12 md:mt-16 text-center max-w-2xl mx-auto flex flex-col items-center gap-6">
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            {lang === "vi" ? "Lộ trình được thiết kế để năng lực của học viên phát triển từng bước thay vì đưa ngay các khái niệm phức tạp vào giai đoạn đầu." : "The pathway is designed for students' capabilities to develop step-by-step rather than introducing complex concepts at the early stages."}
          </p>

          <Button asChild size="lg" className="rounded-full px-8 font-semibold shadow-sm">
            <Link to="/RoboticsLearningPathwayPage">
              {lang === "vi" ? "XEM LỘ TRÌNH ROBOTICS →" : "VIEW THE ROBOTICS PATHWAY →"}
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}